import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { getErrorMessageFromCode } from "utils/CarrotErrors"
import { withRouter } from "react-router-dom"
import queryString from "query-string"
import Login from "components/views/login/Login"
import { useDidMount } from "../../../utils/Hooks"
import { FormProvider, useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { redirectToSamlExternalLogin } from "../../../utils/RedirectToSamlExternalLogin"
import CarrotClient from "lib/carrot-api/index"
import L10n from "../../../lib/carrot-l10n"
import { persistEmail, signIn } from "../../../actions/loginActions"
import Settings from "../../../utils/CarrotConfig"
import { useQueryClient } from "react-query"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)
const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const defaultState = {
  username: null,
  password: null,
  submitting: false,
  showForgotPassword: false,
  showPasswordInput: false,
  showEmailInput: true,
  shake: false
}

const LoginContainer = ({ location, history }) => {
  const [state, setState] = useState(defaultState)
  const dispatch = useDispatch()
  const formMethods = useForm({ reValidateMode: "onSubmit" })
  const passwordInputRef = useRef()
  const { handleSubmit, setError } = formMethods
  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.clear()
  }, [])

  const onSubmit = handleSubmit(async (formData) => {
    if (formData.password) {
      await login(formData.email, formData.password)
    } else {
      await lookup(formData.email)
    }
  })

  useDidMount(() => {
    const parsedQueryString = queryString.parse(location.search)
    const errorCode = parsedQueryString.errorCode
    const isErrorPage = errorCode != null

    setState({
      ...state,
      errorMessage: errorCode ? getErrorMessageFromCode(errorCode) : null,
      // If we have an error message from the URL upon loading this view, that means we want to display only the message and "Sign in" button, so showEmailInput should be false
      showEmailInput: !isErrorPage,
      isErrorPage,
      returnUrl: parsedQueryString.ReturnUrl ? parsedQueryString.ReturnUrl : null
    })
  })

  useLayoutEffect(() => {
    if (passwordInputRef.current) {
      passwordInputRef.current.focus()
    }
  }, [state.showEmailInput])

  const lookup = async (email) => {
    if (state.isErrorPage) {
      setState({
        ...state,
        isErrorPage: false,
        showEmailInput: true,
        showForgotPassword: false,
        showPasswordInput: false,
        errorMessage: null
      })
      return
    }

    setState({
      ...state,
      submitting: true,
      shake: false
    })

    let response
    try {
      response = await client.lookup(email)
    } catch (error) {
      setState({
        ...state,
        submitting: false,
        errorMessage: l`client.connectionError`,
        shake: true
      })
      return
    }

    // Employee's company uses SAML
    if (response.employee && response.employee.samlIdpEntityId) {
      redirectToSamlExternalLogin(response.employee.samlIdpEntityId, response.employee.isAdmin, state.returnUrl)
      return
    }

    if (!response.employee) {
      setState({
        ...state,
        submitting: false,
        errorMessage: response.status === "MEMBER_INACTIVE" ? l`client.employeeInactive` : l`client.employeeNotFound`,
        shake: true
      })
      setError("email")
      return
    }

    // Persist current email in Store
    dispatch(persistEmail(email))

    // if employee hasn't registered yet, send them to signup
    if (!response.employee.isRegistered) {
      history.push(`/signup`)
      return
    }

    // Employee exists, so show login view with no error message
    await setState({
      ...state,
      submitting: false,
      showPasswordInput: true,
      showForgotPassword: true,
      errorMessage: null,
      shake: false
    })

    passwordInputRef.current.focus()

    // Set listener for "back" button press, and transforms view into "lookup" state
    history.listen((location, action) => {
      if (action === "POP") {
        setState({
          ...state,
          submitting: false,
          showPasswordInput: false,
          showForgotPassword: false,
          errorMessage: null
        })
      }
    })
  }

  const login = async (email, password) => {
    setState({
      ...state,
      submitting: true,
      shake: false
    })

    try {
      await client.login(email, password)
    } catch (error) {
      setState({
        ...state,
        submitting: false,
        errorMessage: l`client.invalidUsernamePassword`,
        shake: true
      })
      setError("password")
      return
    }

    setState({ ...state, submitting: false })
    dispatch(signIn())
  }

  return (
    <FormProvider {...formMethods}>
      <Login
        errorMessage={state.errorMessage}
        login={login}
        lookup={lookup}
        onSubmit={onSubmit}
        passwordInputRef={passwordInputRef}
        shake={state.shake}
        showEmailInput={state.showEmailInput}
        showForgotPassword={state.showForgotPassword}
        showPasswordInput={state.showPasswordInput}
        submitting={state.submitting}
      />
    </FormProvider>
  )
}

LoginContainer.propTypes = {
  history: PropTypes.object.isRequired
}

export default withRouter(LoginContainer)
